import React, { useState } from 'react';
import { itemsApi, listingsApi } from '../../utils/api';
import { Spinner } from './Spinner';
import { ConfirmationModal } from './ConfirmationModal';

interface ItemDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: any | null;
  onItemUpdated?: (updatedItem: any) => void;
}

export const ItemDetailModal: React.FC<ItemDetailModalProps> = ({
  isOpen,
  onClose,
  item,
  onItemUpdated
}) => {
  // Editing states
  const [editingPrice, setEditingPrice] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editingCondition, setEditingCondition] = useState(false);
  const [editingMaterials, setEditingMaterials] = useState(false);
  const [editingDimensions, setEditingDimensions] = useState(false);
  
  // Form values
  const [newPrice, setNewPrice] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCondition, setNewCondition] = useState<'new' | 'used' | 'salvage'>('used');
  const [newMaterials, setNewMaterials] = useState<string[]>([]);
  const [newDimensions, setNewDimensions] = useState<{
    width: string;
    height: string;
    depth: string;
    units: 'mm' | 'cm' | 'm' | 'in' | 'ft';
  }>({
    width: '',
    height: '',
    depth: '',
    units: 'cm'
  });
  
  const [saving, setSaving] = useState(false);

  if (!isOpen || !item) return null;

  const handleEditPrice = () => {
    setNewPrice(item.price.toString());
    setEditingPrice(true);
  };

  const handleSavePrice = async () => {
    const price = parseFloat(newPrice);
    if (isNaN(price) || price < 0) {
      alert('Please enter a valid price');
      return;
    }

    setSaving(true);
    try {
      const response = await itemsApi.update(item._id, { price });
      if (response.data.success) {
        const updatedItem = { ...item, price };
        if (onItemUpdated) {
          onItemUpdated(updatedItem);
        }
        setEditingPrice(false);
      } else {
        throw new Error(response.data.error?.message || 'Failed to update price');
      }
    } catch (error: any) {
      console.error('Failed to update price:', error);
      alert('Failed to update price. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingPrice(false);
    setNewPrice('');
  };

  // Generic update function
  const updateField = async (field: string, value: any) => {
    setSaving(true);
    try {
      const response = await itemsApi.update(item._id, { [field]: value });
      if (response.data.success) {
        const updatedItem = { ...item, [field]: value };
        if (onItemUpdated) {
          onItemUpdated(updatedItem);
        }
        return true;
      } else {
        throw new Error(response.data.error?.message || `Failed to update ${field}`);
      }
    } catch (error: any) {
      console.error(`Failed to update ${field}:`, error);
      alert(`Failed to update ${field}. Please try again.`);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Title handlers
  const handleEditTitle = () => {
    setNewTitle(item.title);
    setEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!newTitle.trim()) {
      alert('Title cannot be empty');
      return;
    }

    const success = await updateField('title', newTitle.trim());
    if (success) {
      setEditingTitle(false);
    }
  };

  const handleCancelTitle = () => {
    setEditingTitle(false);
    setNewTitle('');
  };

  // Description handlers
  const handleEditDescription = () => {
    setNewDescription(item.description || '');
    setEditingDescription(true);
  };

  const handleSaveDescription = async () => {
    const success = await updateField('description', newDescription.trim());
    if (success) {
      setEditingDescription(false);
    }
  };

  const handleCancelDescription = () => {
    setEditingDescription(false);
    setNewDescription('');
  };

  // Condition handlers
  const handleEditCondition = () => {
    setNewCondition(item.condition);
    setEditingCondition(true);
  };

  const handleSaveCondition = async () => {
    const success = await updateField('condition', newCondition);
    if (success) {
      setEditingCondition(false);
    }
  };

  const handleCancelCondition = () => {
    setEditingCondition(false);
    setNewCondition(item.condition);
  };

  // Materials handlers
  const handleEditMaterials = () => {
    setNewMaterials([...item.materials]);
    setEditingMaterials(true);
  };

  const handleSaveMaterials = async () => {
    const success = await updateField('materials', newMaterials.filter(m => m.trim()));
    if (success) {
      setEditingMaterials(false);
    }
  };

  const handleCancelMaterials = () => {
    setEditingMaterials(false);
    setNewMaterials([]);
  };

  const handleAddMaterial = () => {
    setNewMaterials([...newMaterials, '']);
  };

  const handleRemoveMaterial = (index: number) => {
    setNewMaterials(newMaterials.filter((_, i) => i !== index));
  };

  const handleMaterialChange = (index: number, value: string) => {
    const updated = [...newMaterials];
    updated[index] = value;
    setNewMaterials(updated);
  };

  // Dimensions handlers
  const handleEditDimensions = () => {
    const validUnits = ['mm', 'cm', 'm', 'in', 'ft'] as const;
    const itemUnits = item.dimensions?.units;
    const units = validUnits.includes(itemUnits as any) ? itemUnits as 'mm' | 'cm' | 'm' | 'in' | 'ft' : 'cm';
    
    setNewDimensions({
      width: item.dimensions?.width?.toString() || '',
      height: item.dimensions?.height?.toString() || '',
      depth: item.dimensions?.depth?.toString() || '',
      units: units
    });
    setEditingDimensions(true);
  };

  const handleSaveDimensions = async () => {
    const dimensions = {
      width: parseFloat(newDimensions.width) || 0,
      height: parseFloat(newDimensions.height) || 0,
      depth: parseFloat(newDimensions.depth) || 0,
      units: newDimensions.units
    };

    const success = await updateField('dimensions', dimensions);
    if (success) {
      setEditingDimensions(false);
    }
  };

  const handleCancelDimensions = () => {
    setEditingDimensions(false);
    setNewDimensions({ width: '', height: '', depth: '', units: 'cm' });
  };

  const aiValue = item.aiAnalysis?.estimatedValue;
  const confidence = item.aiAnalysis?.confidence;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal positioning */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        {/* Modal panel */}
        <div className="relative inline-block align-bottom bg-white dark:bg-slate-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
              Item Details
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[80vh] overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Images and Basic Info */}
              <div className="space-y-6">
                {/* Images */}
                {item.images && item.images.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Images</h3>
                    <div className="grid grid-cols-1 gap-4">
                      {item.images.map((imageUrl: string, index: number) => (
                        <div key={index} className="aspect-video bg-slate-200 dark:bg-slate-700 rounded-lg overflow-hidden">
                          <img
                            src={imageUrl}
                            alt={`${item.title} - Image ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Basic Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Title
                      </label>
                      {editingTitle ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                            disabled={saving}
                          />
                          <button
                            onClick={handleSaveTitle}
                            disabled={saving}
                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelTitle}
                            disabled={saving}
                            className="px-3 py-2 bg-slate-300 text-slate-700 rounded-md hover:bg-slate-400"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between group">
                          <p className="text-slate-900 dark:text-slate-100 font-medium">{item.title}</p>
                          <button
                            onClick={handleEditTitle}
                            className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-opacity"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Description
                      </label>
                      {editingDescription ? (
                        <div className="space-y-2">
                          <textarea
                            value={newDescription}
                            onChange={(e) => setNewDescription(e.target.value)}
                            rows={4}
                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                            disabled={saving}
                            placeholder="Enter description..."
                          />
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={handleSaveDescription}
                              disabled={saving}
                              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelDescription}
                              disabled={saving}
                              className="px-3 py-2 bg-slate-300 text-slate-700 rounded-md hover:bg-slate-400"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="group">
                          <div className="flex items-start justify-between">
                            <p className="text-slate-700 dark:text-slate-300 flex-1">
                              {item.description || 'No description provided'}
                            </p>
                            <button
                              onClick={handleEditDescription}
                              className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-opacity ml-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Condition
                        </label>
                        {editingCondition ? (
                          <div className="space-y-2">
                            <select
                              value={newCondition}
                              onChange={(e) => setNewCondition(e.target.value as 'new' | 'used' | 'salvage')}
                              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                              disabled={saving}
                            >
                              <option value="new">New</option>
                              <option value="used">Used</option>
                              <option value="salvage">Salvage</option>
                            </select>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={handleSaveCondition}
                                disabled={saving}
                                className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelCondition}
                                disabled={saving}
                                className="px-3 py-1 bg-slate-300 text-slate-700 text-sm rounded-md hover:bg-slate-400"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="group flex items-center justify-between">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              item.condition === 'new' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' :
                              item.condition === 'used' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300' :
                              'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                            }`}>
                              {item.condition}
                            </span>
                            <button
                              onClick={handleEditCondition}
                              className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-opacity"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                          Status
                        </label>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          item.status === 'listed' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                            : 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Materials
                      </label>
                      {editingMaterials ? (
                        <div className="space-y-2">
                          {newMaterials.map((material, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={material}
                                onChange={(e) => handleMaterialChange(index, e.target.value)}
                                className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                                placeholder="Material name"
                                disabled={saving}
                              />
                              <button
                                onClick={() => handleRemoveMaterial(index)}
                                disabled={saving}
                                className="p-2 text-red-500 hover:text-red-700"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={handleAddMaterial}
                            disabled={saving}
                            className="px-3 py-2 border border-dashed border-slate-300 dark:border-slate-600 rounded-md text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                          >
                            + Add Material
                          </button>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={handleSaveMaterials}
                              disabled={saving}
                              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelMaterials}
                              disabled={saving}
                              className="px-3 py-2 bg-slate-300 text-slate-700 rounded-md hover:bg-slate-400"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="group">
                          <div className="flex items-start justify-between">
                            <div className="flex flex-wrap gap-2 flex-1">
                              {item.materials && item.materials.length > 0 ? (
                                item.materials.map((material: string, index: number) => (
                                  <span key={index} className="px-2 py-1 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded text-sm">
                                    {material}
                                  </span>
                                ))
                              ) : (
                                <span className="text-slate-500 dark:text-slate-400 text-sm">No materials specified</span>
                              )}
                            </div>
                            <button
                              onClick={handleEditMaterials}
                              className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-opacity ml-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Dimensions
                      </label>
                      {editingDimensions ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-4 gap-2">
                            <input
                              type="number"
                              value={newDimensions.width}
                              onChange={(e) => setNewDimensions({...newDimensions, width: e.target.value})}
                              placeholder="Width"
                              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                              disabled={saving}
                            />
                            <input
                              type="number"
                              value={newDimensions.height}
                              onChange={(e) => setNewDimensions({...newDimensions, height: e.target.value})}
                              placeholder="Height"
                              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                              disabled={saving}
                            />
                            <input
                              type="number"
                              value={newDimensions.depth}
                              onChange={(e) => setNewDimensions({...newDimensions, depth: e.target.value})}
                              placeholder="Depth"
                              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                              disabled={saving}
                            />
                            <select
                              value={newDimensions.units}
                              onChange={(e) => {
                                const units = e.target.value as 'mm' | 'cm' | 'm' | 'in' | 'ft';
                                setNewDimensions({...newDimensions, units});
                              }}
                              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                              disabled={saving}
                            >
                              <option value="mm">mm</option>
                              <option value="cm">cm</option>
                              <option value="m">m</option>
                              <option value="in">in</option>
                              <option value="ft">ft</option>
                            </select>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={handleSaveDimensions}
                              disabled={saving}
                              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelDimensions}
                              disabled={saving}
                              className="px-3 py-2 bg-slate-300 text-slate-700 rounded-md hover:bg-slate-400"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="group">
                          <div className="flex items-center justify-between">
                            <p className="text-slate-700 dark:text-slate-300">
                              {item.dimensions ? 
                                `${item.dimensions.width} × ${item.dimensions.height} × ${item.dimensions.depth} ${item.dimensions.units}` :
                                'No dimensions specified'
                              }
                            </p>
                            <button
                              onClick={handleEditDimensions}
                              className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-opacity"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - AI Analysis and Pricing */}
              <div className="space-y-6">
                {/* AI Analysis */}
                {item.aiAnalysis ? (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">AI Analysis</h3>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                          AI Estimated Value
                        </label>
                        <div className="flex items-center space-x-2">
                          <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            ${typeof aiValue === 'number' ? aiValue.toFixed(2) : '0.00'}
                          </span>
                          {typeof confidence === 'number' && (
                            <span className="text-sm text-blue-700 dark:text-blue-300">
                              ({Math.round(confidence * 100)}% confidence)
                            </span>
                          )}
                        </div>
                      </div>

                      {item.aiAnalysis.notes && (
                        <div>
                          <label className="block text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                            AI Notes
                          </label>
                          <p className="text-blue-800 dark:text-blue-200 text-sm">{item.aiAnalysis.notes}</p>
                        </div>
                      )}

                      {item.aiAnalysis.reasoning && (
                        <div>
                          <label className="block text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                            AI Reasoning
                          </label>
                          <p className="text-blue-800 dark:text-blue-200 text-sm">{item.aiAnalysis.reasoning}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">AI Analysis</h3>
                    <div className="bg-slate-50 dark:bg-slate-900/20 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                      <p className="text-slate-600 dark:text-slate-400 text-sm text-center">
                        No AI analysis available for this item
                      </p>
                    </div>
                  </div>
                )}

                {/* Pricing */}
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Pricing</h3>
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-green-900 dark:text-green-100 mb-2">
                        Current Listing Price
                      </label>
                      
                      {editingPrice ? (
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <span className="text-green-700 dark:text-green-300">$</span>
                            <input
                              type="number"
                              value={newPrice}
                              onChange={(e) => setNewPrice(e.target.value)}
                              className="flex-1 px-3 py-2 border border-green-300 dark:border-green-700 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:text-white"
                              placeholder="Enter new price"
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={handleSavePrice}
                              disabled={saving}
                              className="btn-primary text-sm flex items-center"
                            >
                              {saving ? (
                                <>
                                  <Spinner size="sm" className="mr-2" />
                                  Saving...
                                </>
                              ) : (
                                'Save Price'
                              )}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={saving}
                              className="btn-secondary text-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                            ${item.price.toFixed(2)}
                          </span>
                          <button
                            onClick={handleEditPrice}
                            className="btn-secondary text-sm flex items-center"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit Price
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Price Comparison */}
                    {typeof aiValue === 'number' && aiValue !== item.price && (
                      <div className="pt-3 border-t border-green-200 dark:border-green-800">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-green-700 dark:text-green-300">AI Suggested:</span>
                          <span className="font-medium text-green-600 dark:text-green-400">${aiValue.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm mt-1">
                          <span className="text-green-700 dark:text-green-300">Your Price:</span>
                          <span className="font-medium text-green-600 dark:text-green-400">${item.price.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm mt-1">
                          <span className="text-green-700 dark:text-green-300">Difference:</span>
                          <span className={`font-medium ${
                            item.price > aiValue ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'
                          }`}>
                            {item.price > aiValue ? '+' : ''}${(item.price - aiValue).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Metadata */}
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">Metadata</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Created:</span>
                      <span className="text-slate-900 dark:text-slate-100">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Currency:</span>
                      <span className="text-slate-900 dark:text-slate-100">{item.currency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Item ID:</span>
                      <span className="text-slate-900 dark:text-slate-100 font-mono text-xs">{item._id}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 p-6 border-t border-slate-200 dark:border-slate-700">
            <button
              onClick={onClose}
              className="btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
